import React, { useState, useEffect } from 'react';
import { IoInformationCircleOutline, IoCheckmarkOutline, IoPlayCircleOutline, IoBookOutline, IoCopyOutline } from 'react-icons/io5';
import { HiChevronRight } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

export default function Withdrawal() {
    const { t } = useTranslation();
    const [selectedCoin, setSelectedCoin] = useState('BTC');
    const [selectedChain, setSelectedChain] = useState('Ethereum');
    const [riskAccepted, setRiskAccepted] = useState(false);
    const [copiedState, setCopiedState] = useState(false);
    const [amount, setAmount] = useState('');
    const [walletAddress, setWalletAddress] = useState('');
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    // Coin seçenekleri
    const coins = [
        {
            symbol: 'BTC',
            name: 'Bitcoin',
            network: 'BTC Network',
            icon: '/assets/images/cryptos/bitcoin-btc-logo.png'
        },
        {
            symbol: 'USDT',
            name: 'Tether',
            network: 'TRC20 Network',
            icon: '/assets/images/cryptos/tether-usdt-logo.png'
        },
        {
            symbol: 'ETH',
            name: 'Ethereum',
            network: 'ERC20 Network',
            icon: '/assets/images/cryptos/ethereum-eth-logo.png'
        },
        {
            symbol: 'SOL',
            name: 'Solana',
            network: 'SOLANA Network',
            icon: '/assets/images/cryptos/solana-sol-logo.png'
        }
    ];

    // Network seçenekleri
    const networks = [
        { name: 'Ethereum', address: '0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6' },
        { name: 'BSC', address: '0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6' },
        { name: 'Polygon', address: '0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6' }
    ];

    const selectedCoinData = coins.find(coin => coin.symbol === selectedCoin);
    const selectedNetworkData = networks.find(network => network.name === selectedChain);

    // Get user info on component mount
    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                setLoading(true);
                const response = await api.getUserInfo();
                const userData = response.data?.data || response.data;
                // Map camelCase to PascalCase for consistency
                const normalizedData = {
                    ...userData,
                    Balance: userData.Balance ?? userData.balance,
                    Credit: userData.Credit ?? userData.credit,
                    FullName: userData.FullName ?? userData.fullName
                };
                setUserInfo(normalizedData);
            } catch (error) {
                console.error('Error fetching user info:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchUserInfo();
    }, []);

    const handleCoinChange = (coinSymbol) => {
        setSelectedCoin(coinSymbol);
    };

    const handleNetworkChange = (networkName) => {
        setSelectedChain(networkName);
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(t('withdrawal.toast.addressCopied'));

            // Tik işareti göster
            setCopiedState(true);

            // 3 saniye sonra eski haline döndür
            setTimeout(() => {
                setCopiedState(false);
            }, 3000);
        } catch (err) {
            console.error('Kopyalama hatası:', err);
            toast.error(t('withdrawal.toast.copyFailed'));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!amount || !walletAddress) {
            toast.error(t('withdrawal.toast.fillAllFields'));
            return;
        }

        if (parseFloat(amount) <= 0) {
            toast.error(t('withdrawal.toast.amountGreaterThanZero'));
            return;
        }

        try {
            // CreateCryptoWithdrawal API çağrısı
            const response = await api.createCryptoWithdrawal({
                Asset: selectedCoin,
                WalletAddress: walletAddress,
                Amount: amount.toString()
            });

            if (response.success) {
                toast.success(response.message || t('withdrawal.toast.withdrawalRequestSuccess'));

                // Formu sıfırla
                setAmount('');
                setWalletAddress('');
            } else {
                toast.error(response.message || t('withdrawal.toast.withdrawalFailed'));
            }
        } catch (error) {
            console.error('Para çekme hatası:', error);
            
            // Özel hata mesajı kontrolü - "You can make your next withdrawal request starting from" içeriyorsa
            if (error.message && error.message.includes('You can make your next withdrawal request starting from')) {
                // Tarih kısmını çıkar (MM/DD/YYYY HH:MM:SS formatından DD/MM/YYYY HH:MM:SS formatına çevir)
                const dateMatch = error.message.match(/(\d{2})\/(\d{2})\/(\d{4}) (\d{2}:\d{2}:\d{2})/);
                if (dateMatch) {
                    const [, month, day, year, time] = dateMatch;
                    const formattedDate = `${day}/${month}/${year} ${time}`;
                    toast.error(t('withdrawal.toast.nextWithdrawalTime', { date: formattedDate }));
                } else {
                    toast.error(t('withdrawal.toast.nextWithdrawalTime', { date: error.message }));
                }
            } else {
                toast.error(t('withdrawal.toast.withdrawalFailed'));
            }
        }
    };

    return (
        <div className='deposit-layout'>
            <div className="container py-lg-5">
                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <div className="deposit-page">
                            {/* Header */}
                            <div className="deposit-header">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-3">
                                        <h1 className="deposit-title">{t('withdrawal.title')}</h1>
                                    </div>
                                    <Link to="/withdrawal-bank" className="p2p-link d-flex align-items-center gap-2">
                                        <span>{t('withdrawal.bankWithdrawalLink')}</span>
                                        <HiChevronRight size={16} />
                                    </Link>
                                </div>
                            </div>

                            <div className="row g-4">
                                {/* Sol Kolon - Para Çekme İşlemleri */}
                                <div className="container">
                                    {/* Yatay Varlıklar Kartı */}
                                    <div className="assets-card-horizontal">
                                        <div className="assets-horizontal-grid">
                                            <div className="asset-balance-item left">
                                                <div className="asset-balance-label">
                                                    <span className="asset-dot available"></span>
                                                    {t('deposit.assets.title')}
                                                </div>
                                                <div className="asset-balance-value">
                                                    {loading ? '...' : `${((userInfo?.Balance || 0) + (userInfo?.Credit || 0)).toFixed(2)} USD`}
                                                </div>
                                            </div>

                                            <div className="asset-total-center">
                                                <div className="asset-total-label">{t('deposit.assets.availableBalance')}</div>
                                                <div className="asset-total-amount">
                                                    <span>{loading ? '...' : `${(userInfo?.Balance || 0).toFixed(2)} USD`}</span>
                                                </div>
                                            </div>

                                            <div className="asset-balance-item right">
                                                <div className="asset-balance-label">
                                                    <span className="asset-dot frozen"></span>
                                                    {t('deposit.assets.availableCredit')}
                                                </div>
                                                <div className="asset-balance-value">
                                                    <span>{loading ? '...' : `${(userInfo?.Credit || 0).toFixed(2)} USD`}</span>
                                                </div>  
                                            </div>
                                        </div>
                                    </div>

                                    <div className="deposit-actions">
                                        {/* 1. Coin Seçimi */}
                                        <div className="deposit-step">
                                            <div className="step-number">1</div>
                                            <div className="step-content">
                                                <h3 className="step-title">{t('withdrawal.steps.selectCoin')}</h3>
                                                <div className="coin-selector mt-lg-3">
                                                    <div className="coin-options">
                                                        {coins.map((coin) => (
                                                            <button
                                                                key={coin.symbol}
                                                                className={`coin-option ${selectedCoin === coin.symbol ? 'active' : ''}`}
                                                                onClick={() => handleCoinChange(coin.symbol)}
                                                            >
                                                                <img src={coin.icon} alt={coin.symbol} width="30" height="30" />
                                                                <div className="coin-option-info">
                                                                    <span className="coin-option-symbol">{coin.symbol}</span>
                                                                    <span className="coin-option-name">{coin.network}</span>
                                                                    {/* <span className="coin-option-network">{coin.network}</span> */}
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 2. Miktar ve Cüzdan Adresi */}
                                        <div className="deposit-step">
                                            <div className="step-number">2</div>
                                            <div className="step-content">
                                                <h3 className="step-title">{t('withdrawal.steps.amountAndAddress')}</h3>

                                                {/* Miktar Girişi */}
                                                <div className="form-group mb-3 mt-lg-3">
                                                    <label className="form-label">{t('withdrawal.form.amountLabel')}</label>
                                                    <div className="currency-input-container">
                                                        <div className="currency-symbol-left">$</div>
                                                        <input
                                                            type="number"
                                                            value={amount}
                                                            onChange={(e) => setAmount(e.target.value)}
                                                            placeholder={t('withdrawal.form.amountPlaceholder')}
                                                            step="0.01"
                                                            min="0"
                                                            className="currency-input"
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                {/* Cüzdan Adresi Girişi */}
                                                <div className="form-group mb-3">
                                                    <label className="form-label">{t('withdrawal.form.walletAddressLabel')}</label>
                                                    <input
                                                        type="text"
                                                        value={walletAddress}
                                                        onChange={(e) => setWalletAddress(e.target.value)}
                                                        placeholder={t('withdrawal.form.walletAddressPlaceholder')}
                                                        className="form-control"
                                                        required
                                                    />
                                                </div>

                                            </div>
                                        </div>

                                        {/* 3. Risk Uyarısı */}
                                        <div className="risk-warning">
                                            <h4 className="risk-title">{t('withdrawal.riskWarning.title')}</h4>
                                            <p className="risk-text">
                                                {t('withdrawal.riskWarning.description')}
                                            </p>
                                            <div className="risk-checkbox">
                                                <input
                                                    type="checkbox"
                                                    id="risk-accept"
                                                    checked={riskAccepted}
                                                    onChange={(e) => setRiskAccepted(e.target.checked)}
                                                />
                                                <label htmlFor="risk-accept">
                                                    {t('withdrawal.riskWarning.acceptWarning')}
                                                </label>
                                            </div>
                                        </div>

                                        {/* Onayla Butonu */}
                                        <div className="deposit-actions-footer">
                                            <button
                                                className={`confirm-btn ${riskAccepted ? 'active' : 'disabled'}`}
                                                disabled={!riskAccepted}
                                                onClick={handleSubmit}
                                            >
                                                {t('withdrawal.confirmButton')}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Sağ Kolon - Bilgiler */}
                                {/* <div className="col-lg-4">
                                    <div className="deposit-info"> */}
                                        
                                        {/* <div className="info-section">
                                            <h3 className="info-title">{t('withdrawal.assets.title')}</h3>
                                            <div className="assets-summary">
                                                <div className="total-assets">
                                                    {loading ? '...' : `${((userInfo?.Balance || 0) + (userInfo?.Credit || 0)).toFixed(2)} USD`}
                                                </div>
                                                <div className="assets-breakdown">
                                                    <div className="asset-item">
                                                        <div className="asset-dot available"></div>
                                                        <span>{t('withdrawal.assets.availableBalance')}:</span>
                                                        <span>{loading ? '...' : `${(userInfo?.Balance || 0).toFixed(2)} USD`}</span>
                                                    </div>
                                                    <div className="asset-item">
                                                        <div className="asset-dot frozen"></div>
                                                        <span>{t('withdrawal.assets.availableCredit')}:</span>
                                                        <span>{loading ? '...' : `${(userInfo?.Credit || 0).toFixed(2)} USD`}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div> */}

                                        {/* Dikkat Edilecek Noktalar */}
                                        {/* <div className="info-section">
                                            <h4 className="info-title">{t('withdrawal.attention.title')}</h4>
                                            <div className="attention-items">
                                                <div className="attention-item">
                                                    <span className="attention-label">{t('withdrawal.attention.minWithdrawal')}:</span>
                                                    <span className="attention-value">10 USD</span>
                                                </div>
                                                <div className="attention-item">
                                                    <span className="attention-label">{t('withdrawal.attention.transactionFee')}:</span>
                                                    <span className="attention-value">%0.1</span>
                                                </div>
                                                <div className="attention-item">
                                                    <span className="attention-label">{t('withdrawal.attention.processingTime')}:</span>
                                                    <span className="attention-value">1-24 saat</span>
                                                </div>
                                            </div>
                                        </div> */}

                                        {/* SSS */}
                                        {/* <div className="info-section">
                                            <div className="faq-header">
                                                <h4 className="info-title">{t('withdrawal.faq.title')}</h4>
                                            </div>
                                            <div className="faq-items">
                                                <div className="faq-item">
                                                    <div className="faq-question">
                                                        <span>{t('withdrawal.faq.processingTime')}</span>
                                                        <IoPlayCircleOutline size={16} />
                                                    </div>
                                                </div>
                                                <div className="faq-item">
                                                    <div className="faq-question">
                                                        <span>{t('withdrawal.faq.minAmount')}</span>
                                                        <IoPlayCircleOutline size={16} />
                                                    </div>
                                                </div>
                                                <div className="faq-item">
                                                    <div className="faq-question">
                                                        <span>{t('withdrawal.faq.feeCalculation')}</span>
                                                        <IoPlayCircleOutline size={16} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div> */}
                                    {/* </div>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Toaster
                position="top-center"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: 'var(--card)',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                    },
                    success: {
                        style: {
                            border: '1px solid #10b981',
                        },
                    },
                    error: {
                        style: {
                            border: '1px solid #ef4444',
                        },
                    },
                }}
            />
        </div>
    );
}
